import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle, Plus, Trash2 } from 'lucide-react';
import { BankQuestion, OnlineExamQuestionsApi } from '@/services/school.api';
import { AcademicClass, AcademicClassesApi, ClassSubjectRow } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Modal } from '@/components/ui/Modal';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/cn';

/** One entry per distinct subject taught to the class — same dedupe pattern as ExamsPage.tsx/OnlineExamsPage.tsx. */
function dedupeBySubject(rows: ClassSubjectRow[]): ClassSubjectRow[] {
  const seen = new Map<string, ClassSubjectRow>();
  for (const r of rows) if (!seen.has(r.subjectId)) seen.set(r.subjectId, r);
  return [...seen.values()];
}

export function QuestionBankPage() {
  const { can } = usePermissions();
  const canWrite = can('/online-exams', 'create');
  const canDelete = can('/online-exams', 'delete');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [classSubjectId, setClassSubjectId] = useState('');
  const [creating, setCreating] = useState(false);

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'question-bank'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];
  const selectedClass = classes.find((c) => c.id === classId);

  const { data: subjectRows = [] } = useQuery({
    queryKey: ['class-subjects', classId],
    queryFn: () => AcademicClassesApi.subjects(classId),
    enabled: !!classId,
  });
  const subjects = dedupeBySubject(subjectRows);

  const { data: questions = [], isLoading } = useQuery({
    queryKey: ['online-exam-questions', sectionId, classSubjectId],
    queryFn: () => OnlineExamQuestionsApi.list({ classSectionId: sectionId || undefined, classSubjectId: classSubjectId || undefined }),
  });

  return (
    <>
      <PageHeader
        title="Question Bank"
        description="Reusable MCQ questions, authored once per class section + subject and linked into as many Online Exams as needed, each at its own marks weighting."
        actions={
          canWrite && (
            <button className="btn-primary" onClick={() => setCreating(true)}>
              <Plus className="mr-1.5 h-4 w-4" /> New question
            </button>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select
          className="!w-48"
          value={classId}
          onChange={(e) => {
            setClassId(e.target.value);
            setSectionId('');
            setClassSubjectId('');
          }}
        >
          <option value="">All classes</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </Select>
        <Select className="!w-40" value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId}>
          <option value="">All sections</option>
          {(selectedClass?.sections ?? []).map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </Select>
        <Select className="!w-48" value={classSubjectId} onChange={(e) => setClassSubjectId(e.target.value)} disabled={!classId}>
          <option value="">All subjects</option>
          {subjects.map((s) => (
            <option key={s.classSubjectId} value={s.classSubjectId}>{s.subjectName}</option>
          ))}
        </Select>
      </div>

      {isLoading ? (
        <div className="card p-8 text-center text-slate-400">Loading…</div>
      ) : questions.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">
          No questions yet. {canWrite && (
            <>
              Click <span className="font-medium">New question</span> to author one.
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {questions.map((q) => (
            <QuestionCard key={q.id} question={q} canWrite={canWrite} canDelete={canDelete} />
          ))}
        </div>
      )}

      {creating && (
        <CreateQuestionModal
          classes={classes}
          initialClassId={classId}
          initialSectionId={sectionId}
          initialClassSubjectId={classSubjectId}
          onClose={() => setCreating(false)}
        />
      )}
    </>
  );
}

function QuestionCard({ question, canWrite, canDelete }: { question: BankQuestion; canWrite: boolean; canDelete: boolean }) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['online-exam-questions'] });

  const removeQuestion = useMutation({
    mutationFn: () => OnlineExamQuestionsApi.remove(question.id),
    onSuccess: invalidate,
  });
  const markCorrect = useMutation({
    mutationFn: (optionId: string) => OnlineExamQuestionsApi.updateOption(question.id, optionId, { isAnswer: true }),
    onSuccess: invalidate,
  });
  const removeOption = useMutation({
    mutationFn: (optionId: string) => OnlineExamQuestionsApi.removeOption(question.id, optionId),
    onSuccess: invalidate,
  });

  const [removeErr, setRemoveErr] = useState<string | undefined>();

  return (
    <div className="card p-4">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <div className="text-xs text-slate-400">{question.className} · {question.sectionName} · {question.subjectName}</div>
          <div className="text-sm font-medium text-slate-900">{question.questionText}</div>
          {question.note && <div className="mt-1 text-xs italic text-slate-500">Note: {question.note}</div>}
        </div>
        {canDelete && (
          <button
            className="rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
            onClick={() => {
              setRemoveErr(undefined);
              removeQuestion.mutate(undefined, { onError: (e) => setRemoveErr(errMsg(e)) });
            }}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      {removeErr && <div className="mb-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{removeErr}</div>}
      <ul className="space-y-1.5">
        {question.options.map((o) => (
          <li key={o.id} className="flex items-center justify-between gap-2 rounded-md px-2 py-1 text-sm hover:bg-slate-50">
            <span className={cn('inline-flex items-center gap-2', o.isAnswer ? 'font-medium text-green-700' : 'text-slate-600')}>
              {o.isAnswer ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <Circle className="h-4 w-4 text-slate-300" />}
              {o.optionText}
            </span>
            {canWrite && (
              <span className="flex items-center gap-2">
                {!o.isAnswer && (
                  <button className="text-xs text-slate-500 hover:text-brand-600" onClick={() => markCorrect.mutate(o.id)}>
                    Mark correct
                  </button>
                )}
                <button className="text-slate-400 hover:text-red-600" onClick={() => removeOption.mutate(o.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {canWrite && <AddOptionInline questionId={question.id} onAdded={invalidate} />}
    </div>
  );
}

function AddOptionInline({ questionId, onAdded }: { questionId: string; onAdded: () => void }) {
  const [text, setText] = useState('');
  const add = useMutation({
    mutationFn: () => OnlineExamQuestionsApi.addOption(questionId, { optionText: text, isAnswer: false }),
    onSuccess: () => { setText(''); onAdded(); },
  });
  return (
    <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (text.trim()) add.mutate(); }}>
      <Input className="!py-1.5 text-sm" placeholder="Add another option…" value={text} onChange={(e) => setText(e.target.value)} />
      <button type="submit" className="btn-secondary !py-1.5 text-sm" disabled={!text.trim() || add.isPending}>Add</button>
    </form>
  );
}

const questionSchema = z.object({
  classId: z.string().min(1, 'Required'),
  sectionId: z.string().min(1, 'Required'),
  classSubjectId: z.string().min(1, 'Required'),
  questionText: z.string().min(1, 'Required'),
  imageUrl: z.string().optional(),
  note: z.string().optional(),
});
type QuestionForm = z.infer<typeof questionSchema>;

function CreateQuestionModal({
  classes,
  initialClassId,
  initialSectionId,
  initialClassSubjectId,
  onClose,
}: {
  classes: AcademicClass[];
  initialClassId: string;
  initialSectionId: string;
  initialClassSubjectId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<QuestionForm>({
    resolver: zodResolver(questionSchema),
    defaultValues: { classId: initialClassId, sectionId: initialSectionId, classSubjectId: initialClassSubjectId },
  });
  const classId = watch('classId');
  const selectedClass = classes.find((c) => c.id === classId);

  const { data: subjectRows = [] } = useQuery({
    queryKey: ['class-subjects', classId],
    queryFn: () => AcademicClassesApi.subjects(classId),
    enabled: !!classId,
  });
  const subjects = dedupeBySubject(subjectRows);

  const [options, setOptions] = useState<{ text: string; isAnswer: boolean }[]>([
    { text: '', isAnswer: true },
    { text: '', isAnswer: false },
    { text: '', isAnswer: false },
    { text: '', isAnswer: false },
  ]);
  const [optionErr, setOptionErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (f: QuestionForm) =>
      OnlineExamQuestionsApi.create({
        classSectionId: f.sectionId,
        classSubjectId: f.classSubjectId,
        questionText: f.questionText,
        imageUrl: f.imageUrl || undefined,
        note: f.note || undefined,
        options: options.map((o) => ({ optionText: o.text, isAnswer: o.isAnswer })),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['online-exam-questions'] });
      onClose();
    },
  });

  const setOptionText = (i: number, text: string) => setOptions((os) => os.map((o, idx) => (idx === i ? { ...o, text } : o)));
  const setCorrect = (i: number) => setOptions((os) => os.map((o, idx) => ({ ...o, isAnswer: idx === i })));
  const addOption = () => setOptions((os) => [...os, { text: '', isAnswer: false }]);
  const removeOptionAt = (i: number) => setOptions((os) => (os.length <= 2 ? os : os.filter((_, idx) => idx !== i)));

  const onSubmit = (f: QuestionForm) => {
    setOptionErr(null);
    const filled = options.filter((o) => o.text.trim().length > 0);
    if (filled.length < 2) {
      setOptionErr('At least 2 options are required.');
      return;
    }
    if (!filled.some((o) => o.isAnswer)) {
      setOptionErr('Select which option is the correct answer.');
      return;
    }
    create.mutate(f);
  };

  return (
    <Modal open onClose={onClose} title="New question" size="lg">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Class</label>
            <Select {...register('classId')}>
              <option value="">Select class</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
            {errors.classId && <FieldErr msg={errors.classId.message} />}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Section</label>
            <Select {...register('sectionId')} disabled={!classId}>
              <option value="">{classId ? 'Select section' : 'Pick a class first'}</option>
              {(selectedClass?.sections ?? []).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </Select>
            {errors.sectionId && <FieldErr msg={errors.sectionId.message} />}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Subject</label>
            <Select {...register('classSubjectId')} disabled={!classId}>
              <option value="">{classId ? 'Select subject' : 'Pick a class first'}</option>
              {subjects.map((s) => (
                <option key={s.classSubjectId} value={s.classSubjectId}>{s.subjectName}</option>
              ))}
            </Select>
            {errors.classSubjectId && <FieldErr msg={errors.classSubjectId.message} />}
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Question</label>
          <Textarea rows={2} placeholder="What is the capital of France?" {...register('questionText')} />
          {errors.questionText && <FieldErr msg={errors.questionText.message} />}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Image URL <span className="font-normal text-slate-400">(optional)</span></label>
          <Input placeholder="https://…" {...register('imageUrl')} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Note <span className="font-normal text-slate-400">(optional — solution explanation, teacher/admin-only, never shown to students)</span></label>
          <Textarea rows={2} {...register('note')} />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Options <span className="font-normal text-slate-400">— select the radio button for the correct answer</span>
          </label>
          <div className="space-y-2">
            {options.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="correct-option"
                  checked={o.isAnswer}
                  onChange={() => setCorrect(i)}
                  className="h-4 w-4 text-brand-600 focus:ring-brand-500"
                />
                <Input placeholder={`Option ${i + 1}`} value={o.text} onChange={(e) => setOptionText(i, e.target.value)} />
                {options.length > 2 && (
                  <button type="button" className="text-slate-400 hover:text-red-600" onClick={() => removeOptionAt(i)}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button type="button" className="btn-secondary mt-2 !py-1.5 text-sm" onClick={addOption}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Add option
          </button>
        </div>

        {optionErr && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{optionErr}</div>}
        {create.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(create.error)}</div>}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={create.isPending}>
            {create.isPending ? 'Saving…' : 'Add question'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function FieldErr({ msg }: { msg?: string }) {
  return <p className="mt-1 text-xs text-red-600">{msg}</p>;
}

function errMsg(e: unknown): string | undefined {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}
